import { GetWorklistQueryVariables, WorklistItemStatus } from 'generated/graphql';
import type {
  ProcessedFilterStateType,
  ProcessedNumberRangeFilterValueType,
  ProcessedTextFilterValueType,
} from '../../WorklistFilters/types';
import { WorklistSurface, type WorklistSurfaceType } from '../types';

import { useWorklistFiltersContext } from '../../WorklistFilters/WorklistFiltersContext';
import { useWorklistSortContext } from '../contexts/WorklistSortContext';
import { ageRangeToDOBRange } from '../../WorklistFilters/utils';

import { useMemo } from 'react';
import { FF, useFeatureFlagEnabled } from 'modules/feature-flags';
import { useRecoilValue } from 'recoil';
import { activeCustomWorklistIDsState } from '../state';
import { useCustomWorklists } from './useCustomWorklists';
import { WithApolloContext } from 'modules/Apollo/types';

export const WORKLIST_QUERY_LIMIT = 50;

export const IN_PROGRESS_WORKLIST_STATUSES = [
  WorklistItemStatus.InProgress,
  WorklistItemStatus.InProgressAddendum,
] as const;

export const STATUS_MAP = {
  [WorklistSurface.STUDIES_DIALOG]: null,
  [WorklistSurface.PATIENT_JACKET_PRIORS]: null,
  [WorklistSurface.PLACEHOLDER]: null,
} as const;

type useWorklistVariablesProps = WithApolloContext & {
  userId: Nullable<string>;
  search: string;
  surface: WorklistSurfaceType;
  processedFilters: ProcessedFilterStateType;
  skipCustomWorklistsQuery?: boolean;
};

export const useWorklistVariables = ({
  userId,
  search,
  surface,
  processedFilters,
  skipCustomWorklistsQuery = false,
  context,
}: useWorklistVariablesProps): GetWorklistQueryVariables => {
  const { sortColumns: sortColumnsV1, sortOrders: sortOrdersV1 } = useWorklistFiltersContext();
  const { sortColumns: sortColumnsV2, sortOrders: sortOrdersV2 } = useWorklistSortContext();
  const [isRefactorEnabled] = useFeatureFlagEnabled(FF.WORKLIST_CONTEXT_REFACTOR);
  const activeCustomWorklistIDs = useRecoilValue(activeCustomWorklistIDsState);
  const { allWorklists } = useCustomWorklists({
    queryOptions: { context, skip: skipCustomWorklistsQuery },
  });

  const { sortColumns, sortOrders } = useMemo(() => {
    if (isRefactorEnabled && surface === WorklistSurface.CUSTOM_WORKLIST) {
      return {
        sortColumns: sortColumnsV2,
        sortOrders: sortOrdersV2,
      };
    }
    return {
      sortColumns: sortColumnsV1,
      sortOrders: sortOrdersV1,
    };
  }, [isRefactorEnabled, surface, sortColumnsV1, sortColumnsV2, sortOrdersV1, sortOrdersV2]);

  const selectedStatus = processedFilters.status as ProcessedTextFilterValueType;

  const processedAgeData = useMemo(
    () =>
      processedFilters.age != null
        ? ageRangeToDOBRange(processedFilters.age as ProcessedNumberRangeFilterValueType)
        : null,
    [processedFilters.age]
  );

  const shouldUseStatsToTop = useMemo(() => {
    if (activeCustomWorklistIDs.length === 0) {
      return false;
    }

    return activeCustomWorklistIDs.some((worklistId) => {
      const worklist = allWorklists.get(worklistId);
      return worklist?.advancedSettings?.statsToTop === true;
    });
  }, [activeCustomWorklistIDs, allWorklists]);

  const shouldUsePrioritySorting = useMemo(() => {
    return surface === WorklistSurface.CUSTOM_WORKLIST && shouldUseStatsToTop;
  }, [surface, shouldUseStatsToTop]);

  const claimedBy = useMemo(() => {
    if (Array.isArray(processedFilters.claimedBy) && processedFilters.claimedBy.length > 0) {
      return processedFilters.claimedBy[0];
    }
    return null;
  }, [processedFilters]);

  return useMemo(
    () =>
      ({
        limit: WORKLIST_QUERY_LIMIT,
        claimedBy,
        sortOrder: sortOrders,
        sortColumn: sortColumns,
        status: processedFilters.examStatus,
        filter: search,
        patientSex: processedFilters.patientSex,
        patientDob: processedFilters.patientDob ?? processedAgeData ?? undefined,
        studyDate: processedFilters.studyDate,
        submittedAt: processedFilters.submittedAt,
        modality: processedFilters.modality,
        priority: shouldUsePrioritySorting ? ['-priority'] : [],
        activeViews: processedFilters.views,
        hasDraftReport: processedFilters.hasDraftReport,
        customStatus: processedFilters.customStatus,
        accessionNumberPartial: processedFilters.accessionNumber,
        mrnPartial: processedFilters.mrn,
        isActive: undefined,
        patientType: processedFilters.patientType,
        studyDescription: processedFilters.studyDescription,
        site: processedFilters.site,
        bodyPart: processedFilters.bodyPart,
        patientName: processedFilters.patientName,
        priorityFilter: processedFilters.priority,

        referringPhysicianPartial: processedFilters.referringPhysician
          ? [processedFilters.referringPhysician]
          : undefined,

        claimedByPartial: processedFilters.radiologist ? [processedFilters.radiologist] : undefined,
        signedBy: processedFilters.signedBy,
        examCode: processedFilters.examCode,
        specialty: processedFilters.specialty,
        reportExtendedStatus: processedFilters.reportExtendedStatus,
        criticalResult: processedFilters.criticalResult,
      }) as GetWorklistQueryVariables,
    [
      claimedBy,
      sortOrders,
      sortColumns,
      processedFilters.examStatus,
      processedFilters.patientSex,
      processedFilters.patientDob,
      processedFilters.studyDate,
      processedFilters.submittedAt,
      processedFilters.modality,
      processedFilters.views,
      processedFilters.hasDraftReport,
      processedFilters.customStatus,
      processedFilters.accessionNumber,
      processedFilters.mrn,
      processedFilters.patientType,
      processedFilters.studyDescription,
      processedFilters.site,
      processedFilters.bodyPart,
      processedFilters.patientName,
      processedFilters.priority,
      processedFilters.referringPhysician,
      processedFilters.radiologist,
      processedFilters.signedBy,
      processedFilters.examCode,
      processedFilters.specialty,
      processedFilters.reportExtendedStatus,
      processedFilters.criticalResult,
      search,
      processedAgeData,
      shouldUsePrioritySorting,
    ]
  );
};
